import { useState } from "react";
import { Phone, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PHONE_DISPLAY, PHONE_TEL, TECH_EMERGENCY_URL } from "../../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", suburb: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name,
                    phone: form.phone,
                    message: form.message,
                    ...(form.email ? { email: form.email } : {}),
                    ...(form.suburb ? { suburb: form.suburb } : {}),
                }),
            });
            if (!res.ok) throw new Error("Request failed");
            toast.success("Thanks! Your enquiry has been sent. We'll be in touch shortly.");
            setForm({ name: "", phone: "", email: "", suburb: "", message: "" });
        } catch {
            toast.error(`Sorry, something went wrong. Please call us on ${PHONE_DISPLAY} instead.`);
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";

    return (
        <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input id="contact-name" data-testid="contact-name-input" name="name" required value={form.name} onChange={handleChange} className={inputClass} placeholder="Your name" />
                </div>
                <div>
                    <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                    <input id="contact-phone" data-testid="contact-phone-input" name="phone" type="tel" required value={form.phone} onChange={handleChange} className={inputClass} placeholder="Your phone number" />
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input id="contact-email" data-testid="contact-email-input" name="email" type="email" value={form.email} onChange={handleChange} className={inputClass} placeholder="you@example.com (optional)" />
                </div>
                <div>
                    <label htmlFor="contact-suburb" className="block text-sm font-medium text-slate-700 mb-1">Suburb</label>
                    <input id="contact-suburb" data-testid="contact-suburb-input" name="suburb" value={form.suburb} onChange={handleChange} className={inputClass} placeholder="e.g. Semaphore (optional)" />
                </div>
            </div>
            <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-slate-700 mb-1">How can we help? *</label>
                <textarea id="contact-message" data-testid="contact-message-input" name="message" required rows={4} value={form.message} onChange={handleChange} className={inputClass} placeholder="Tell us a little about the problem..." />
            </div>
            <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] disabled:opacity-60 text-white font-semibold rounded-full px-8 py-3 transition-colors shadow-lg shadow-orange-500/20"
            >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Sending..." : "Send Enquiry"}
            </button>
            <p className="text-xs text-slate-500">
                Prefer to talk? Call <a href={PHONE_TEL} data-testid="contact-form-phone-link" className="font-semibold text-blue-600 underline">{PHONE_DISPLAY}</a>
            </p>
        </form>
    );
};

const DetailRow = ({ label, children, testId }) => (
    <div className="border-t border-slate-200 py-3">
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</p>
        <div data-testid={testId} className="text-sm text-slate-700">{children}</div>
    </div>
);

export const Contact = () => (
    <section id="contact" data-testid="contact-section" className="bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-16">
            <div className="grid lg:grid-cols-2 gap-10">
                <div>
                    <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#0F2E52] mb-2">
                        Need Computer Help?
                    </h2>
                    <p className="text-base md:text-lg text-slate-700 mb-3">The fastest way to get help is to call:</p>
                    <a
                        href={PHONE_TEL}
                        data-testid="contact-phone-link"
                        className="inline-flex items-center gap-2 text-2xl font-bold text-[#0F2E52] hover:text-[#ea580c] transition-colors mb-6"
                    >
                        <Phone className="w-5 h-5" />
                        {PHONE_DISPLAY}
                    </a>
                    <div>
                        <DetailRow label="Service Area" testId="contact-service-area">
                            Semaphore SA 5019 and surrounding western suburbs — onsite at your home or business.
                        </DetailRow>
                        <DetailRow label="Opening Hours" testId="contact-opening-hours">
                            Monday – Friday: 9:00am – 6:00pm<br />
                            Saturday: 9:00am – 3:00pm<br />
                            Sunday: Closed
                        </DetailRow>
                        <DetailRow label="Payment" testId="contact-payment">
                            Easy payments onsite — EFTPOS or cash accepted once the job is completed.
                        </DetailRow>
                        <DetailRow label="Operated By" testId="contact-operated-by">
                            <a
                                href={TECH_EMERGENCY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-tech-emergency-link"
                                className="text-blue-600 font-medium hover:underline"
                            >
                                Tech Emergency
                            </a>
                        </DetailRow>
                    </div>
                </div>
                <ContactForm />
            </div>
        </div>
    </section>
);
